'use client'

import { ChevronLeft, ChevronRight } from 'lucide-react'
import * as React from 'react'
import { DayPicker } from 'react-day-picker'
import { cn } from '../../lib/utils'
import { ButtonVariants } from './button/variants'

export type CalendarProps = React.ComponentProps<typeof DayPicker>

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps): JSX.Element {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('~p-4', className)}
      classNames={{
        months:
          '~flex ~flex-col sm:~flex-row ~space-y-4 sm:~space-x-4 sm:~space-y-0',
        month: '~space-y-4 ~w-full',
        caption: '~flex ~justify-center ~pt-1 ~relative ~items-center',
        caption_label: '~text-sm ~font-medium',
        nav: '~space-x-1 ~flex ~items-center',
        nav_button: cn(
          ButtonVariants.base({ variant: 'outline' }),
          '~h-7 ~w-7 ~bg-transparent ~p-0 ~opacity-50 hover:~opacity-100'
        ),
        nav_button_previous: '~absolute ~left-1 !~rounded-3xs',
        nav_button_next: '~absolute ~right-1 !~rounded-3xs',
        table: '~w-full ~border-collapse ~space-y-1',
        head_row: '~flex',
        head_cell:
          '~text-muted-foreground ~rounded-xs ~w-[36px] ~font-normal ~text-[0.8rem] ~text-center',
        row: '~flex ~w-full ~mt-2',
        cell:
          '~h-[36px] ~w-[36px] ~text-center ~text-sm ~p-0 ~relative ~rounded-3xs [&:has([aria-selected].day-range-end)]:~rounded-r-3xs [&:has([aria-selected].day-outside)]:~bg-accent/50 [&:has([aria-selected])]:~bg-accent first:[&:has([aria-selected])]:~rounded-l-3xs last:[&:has([aria-selected])]:~rounded-r-3xs focus-within:~relative focus-within:~z-20',
        day: cn(
          ButtonVariants.base({ variant: 'ghost' }),
          '~h-[36px] ~w-[36px] ~p-0 ~font-normal ~rounded-3xs ~text-sm aria-selected:~opacity-100'
        ),
        day_range_end: '~day-range-end',
        day_selected:
          '~bg-primary ~text-primary-foreground hover:~bg-primary hover:~text-primary-foreground focus:~bg-primary focus:~text-primary-foreground',
        day_today: '~bg-accent ~text-accent-foreground',
        day_outside:
          '~day-outside ~text-muted-foreground aria-selected:~bg-accent/50 aria-selected:~text-muted-foreground',
        day_disabled: '~text-muted-foreground ~opacity-50',
        day_range_middle:
          'aria-selected:~bg-accent aria-selected:~text-accent-foreground',
        day_hidden: '~invisible',
        ...classNames,
      }}
      components={{
        IconLeft: ({ ..._props }) => <ChevronLeft className="~h-4 ~w-4" />,
        IconRight: ({ ..._props }) => <ChevronRight className="~h-4 ~w-4" />,
      }}
      {...props}
    />
  )
}

Calendar.displayName = 'Calendar'

export { Calendar }
